import { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { LogOut, MapPin, Clock, User, CheckCircle, XCircle, Play, Phone, Settings, Navigation, UserCheck, Flag, Euro, Fuel, ArrowLeftRight, Menu, X, FileText, Download, ChevronDown, ChevronUp, Droplets, CalendarDays } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import NotificationBell from '../components/NotificationBell';
import GPSPermissionRequest from '../components/shared/GPSPermissionRequest';
import ActionStatusOverlay from '../components/shared/ActionStatusOverlay';
import DriverRatingCard from '../components/DriverRatingCard';
import DriverWhatsNewModal from '../components/driver/DriverWhatsNewModal';


// Authenticated file download handler
const downloadVoucherWithAuth = async (rideId, voucherId, filename, token) => {
  try {
    const response = await fetch(`${API}/rides/${rideId}/vouchers/${voucherId}/download`, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    if (!response.ok) {
      throw new Error(`Download failed: ${response.status}`);
    }
    
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    
    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'voucher';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    // Clean up the object URL
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading voucher:', error);
    throw error;
  }
};

// Authenticated port file download handler
const downloadPortFileWithAuth = async (rideId, portFileId, filename, token) => {
  try {
    const response = await fetch(`${API}/rides/${rideId}/port-files/${portFileId}/download`, {
      method: 'GET',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!response.ok) throw new Error(`Download failed: ${response.status}`);
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || 'port_file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading port file:', error);
    throw error;
  }
};

export default function DriverDashboard() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [rides, setRides] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  // Admin-drivers default to 'my_rides', regular drivers default to 'in_progress'
  const [filter, setFilter] = useState(user?.role === 'admin-driver' ? 'my_rides' : 'in_progress');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [successOverlay, setSuccessOverlay] = useState(null);
  const [hiddenMenu, setHiddenMenu] = useState([]);

  useEffect(() => {
    axios.get(`${API}/hidden-sections`)
      .then(r => setHiddenMenu(r.data.driver || []))
      .catch(() => {});
  }, []);
  const [expandedRideVouchers, setExpandedRideVouchers] = useState({});
  const [expandedRidePortFiles, setExpandedRidePortFiles] = useState({});
  const [dailyVouchers, setDailyVouchers] = useState([]);
  
  // Check if user has location restrictions (default true if not set)
  const isLocationRestricted = user?.location_restricted !== false;
  
  // Only show GPS permission request if user has location restrictions enabled
  const [showGPSPermission, setShowGPSPermission] = useState(isLocationRestricted);
  
  // Check if user is admin-driver
  const isAdminDriver = user?.role === 'admin-driver';

  // Handle GPS permission granted
  const handleGPSPermissionGranted = () => {
    setShowGPSPermission(false);
    toast.success('GPS location enabled!');
  };

  // Handle GPS permission denied
  const handleGPSPermissionDenied = () => {
    setShowGPSPermission(false);
  };

  // Toggle voucher section for a ride
  const toggleVouchers = (rideId) => {
    setExpandedRideVouchers(prev => ({
      ...prev,
      [rideId]: !prev[rideId]
    }));
  };

  // Toggle port files section for a ride
  const togglePortFiles = (rideId) => {
    setExpandedRidePortFiles(prev => ({
      ...prev,
      [rideId]: !prev[rideId]
    }));
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      // For admin-drivers: 
      // - "my_rides" filter uses /rides/my-rides (today's rides assigned to me)
      // - Other filters use /rides/today (today's rides for all drivers)
      // For regular drivers: always use /rides (their assigned rides only)
      let ridesEndpoint;
      
      if (isAdminDriver) {
        ridesEndpoint = filter === 'my_rides' 
          ? `${API}/rides/my-rides`
          : `${API}/rides/today`;
      } else {
        ridesEndpoint = `${API}/rides`;
      }
      
      // Load rides first and unblock the UI; stats and vouchers load in background
      const ridesRes = await axios.get(ridesEndpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRides(ridesRes.data);

      axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => setStats(r.data)).catch(e => console.warn('Stats fetch failed:', e));

      // Fetch daily vouchers for today (non-blocking)
      const now = new Date();
      const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
      axios.get(`${API}/daily-vouchers?date=${today}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => setDailyVouchers(r.data)).catch(e => console.warn('Daily vouchers fetch failed:', e));
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (rideId, newStatus) => {
    try {
      await axios.put(
        `${API}/rides/${rideId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Ride status updated to ${newStatus.replace('_', ' ')}`);
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const markArrivedAtPickup = async (rideId) => {
    try {
      await axios.put(`${API}/rides/${rideId}/arrived-at-pickup`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessOverlay('arrived_pickup');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || (error.response ? `Failed to update (HTTP ${error.response.status})` : 'Server unreachable - check your connection'));
    }
  };

  const markPickedUpCustomer = async (rideId) => {
    try {
      await axios.put(`${API}/rides/${rideId}/picked-up-customer`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessOverlay('picked_up');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || (error.response ? `Failed to update (HTTP ${error.response.status})` : 'Server unreachable - check your connection'));
    }
  };

  const markArrivedAtDropoff = async (rideId) => {
    try {
      await axios.put(`${API}/rides/${rideId}/arrived-at-dropoff`, {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccessOverlay('completed');
      fetchData();
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || (error.response ? `Failed to update (HTTP ${error.response.status})` : 'Server unreachable - check your connection'));
    }
  };

  const filteredRides = rides.filter(ride => {
    // For admin-driver: data is already filtered to today's rides from backend
    // "my_rides" = today's rides for current user (already filtered)
    // Other filters = today's rides for all drivers (need to filter by status)
    if (isAdminDriver && filter === 'my_rides') return true;
    if (isAdminDriver && filter === 'all') return true;
    
    if (filter === 'all') return true;
    // "My Rides" filter for regular drivers
    if (filter === 'my_rides') {
      return ride.assigned_driver_id === user?.id;
    }
    if (filter === 'active') return ['active', 'in_progress'].includes(ride.status);
    if (filter === 'in_progress') return ride.status === 'in_progress';
    return ride.status === filter;
  });
  
  // Sort rides based on filter
  const sortedFilteredRides = [...filteredRides].sort((a, b) => {
    // Data from backend endpoints is already sorted for admin-driver
    if (isAdminDriver) return 0;
    
    // For "my_rides" filter, use specific order: in_progress, active, no_show, cancelled
    if (filter === 'my_rides') {
      const statusOrder = { 'in_progress': 0, 'active': 1, 'no_show': 2, 'cancelled': 3 };
      const statusDiff = (statusOrder[a.status] ?? 99) - (statusOrder[b.status] ?? 99);
      if (statusDiff !== 0) return statusDiff;
      // Then sort by date
      return new Date(a.date_time) - new Date(b.date_time);
    }
    // Default sorting: in_progress rides first, then by date
    if (a.status === 'in_progress' && b.status !== 'in_progress') return -1;
    if (b.status === 'in_progress' && a.status !== 'in_progress') return 1;
    return new Date(a.date_time) - new Date(b.date_time);
  });

  // Categorize rides into Today, Tomorrow, Future, and Past
  const categorizeRides = (rides) => {
    const now = new Date();
    const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    
    const tomorrowDate = new Date(now);
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrowStr = `${tomorrowDate.getFullYear()}-${String(tomorrowDate.getMonth() + 1).padStart(2, '0')}-${String(tomorrowDate.getDate()).padStart(2, '0')}`;

    const todayRides = [];
    const tomorrowRides = [];
    const futureRides = [];
    const pastRides = [];

    rides.forEach(ride => {
      if (!ride.date_time) {
        // Rides without dates go to past
        pastRides.push(ride);
        return;
      }
      
      // Convert ride date_time to LOCAL date for correct categorization
      const rd = new Date(ride.date_time);
      const rideDateStr = `${rd.getFullYear()}-${String(rd.getMonth() + 1).padStart(2, '0')}-${String(rd.getDate()).padStart(2, '0')}`;
      
      // IMPORTANT: Active or in_progress rides from past dates should appear in TODAY's rides
      // so they remain visible and actionable
      const isActiveOrInProgress = ride.status === 'active' || ride.status === 'in_progress';
      const isPastDate = rideDateStr < todayStr;

      if (isPastDate && isActiveOrInProgress) {
        // Past date but still active/in_progress - show in today's rides
        todayRides.push(ride);
      } else if (rideDateStr === todayStr) {
        todayRides.push(ride);
      } else if (rideDateStr === tomorrowStr) {
        tomorrowRides.push(ride);
      } else if (rideDateStr > tomorrowStr) {
        futureRides.push(ride);
      } else if (isPastDate) {
        // Past date with completed/cancelled/no_show status
        pastRides.push(ride);
      }
    });

    // Reverse future rides so closest date appears first
    futureRides.reverse();

    return { todayRides, tomorrowRides, futureRides, pastRides };
  };

  const { todayRides, tomorrowRides, futureRides, pastRides } = categorizeRides(sortedFilteredRides);

  // Function to render individual ride card
  const renderRideCard = (ride) => (
    <div
      key={ride.id}
      className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm hover:shadow-md transition-shadow"
      data-testid={`ride-card-${ride.id}`}
    >
      {/* Clickable header to open ride view */}
      <div 
        onClick={() => navigate(`/driver/ride/${ride.id}`)}
        className="p-6 cursor-pointer hover:bg-slate-50 transition-colors"
      >
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex-1 space-y-3">
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <h3 className="text-xl font-semibold text-slate-900">{ride.customer_name}</h3>
                  <span className={`status-badge ${getStatusColor(ride.status)}`} data-testid={`status-badge-${ride.status}`}>
                    {ride.status.replace('_', ' ')}
                  </span>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-slate-500 flex items-center">
                    <Clock className="h-4 w-4 mr-1" />
                    {formatDateTime(ride.date_time)}
                  </p>
                </div>
              </div>
            </div>

            {/* Partner Name Display */}
            {ride.partner && (
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-purple-100 text-purple-800 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <User className="h-3 w-3" />
                  Partner: {ride.partner}
                </div>
              </div>
            )}

            {/* Assigned Driver Name Display - Only for admin-drivers viewing all rides */}
            {isAdminDriver && ride.assigned_driver_name && filter !== 'my_rides' && (
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                  <User className="h-3 w-3" />
                  Driver: {ride.assigned_driver_name}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-3">
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-xs text-slate-400 uppercase">Pickup</p>
                  <p className="text-sm text-slate-700 font-medium">{ride.pickup_location || 'N/A'}</p>
                </div>
              </div>
              <div className="flex items-start gap-2">
                <div className="w-2 h-2 bg-amber-500 rounded-full mt-2 flex-shrink-0"></div>
                <div>
                  <p className="text-xs text-slate-400 uppercase">Dropoff</p>
                  <p className="text-sm text-slate-700 font-medium">{ride.dropoff_location || 'N/A'}</p>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 text-xs text-slate-500">
              {ride.vehicle_type && (
                <span className="bg-slate-100 px-2 py-1 rounded">{ride.vehicle_type}</span>
              )}
              {ride.number_of_people && (
                <span className="bg-slate-100 px-2 py-1 rounded">{ride.number_of_people} pax</span>
              )}
              {ride.flight_number && (
                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded">✈️ {ride.flight_number}</span>
              )}
            </div>
            {/* Extras */}
            {ride.extras && ride.extras.length > 0 && (
              <div className="flex flex-wrap gap-2 text-xs" data-testid="ride-extras">
                {ride.extras.map((extra, i) => (
                  <span key={i} className="bg-purple-50 text-purple-700 px-2 py-1 rounded">
                    {extra}
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Open Ride Button */}
          <div className="flex-shrink-0">
            <Button
              className="bg-amber-500 hover:bg-amber-600 text-white rounded-xl px-6"
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/driver/ride/${ride.id}`);
              }}
            >
              <Navigation className="h-4 w-4 mr-2" />
              Open Ride
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions Bar */}
      {ride.status !== 'completed' && ride.status !== 'cancelled' && (
        <div className="border-t border-slate-100 px-6 py-3 bg-slate-50 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-slate-500">
            {ride.arrived_at_pickup && <span className="text-emerald-600">✓ At pickup</span>}
            {ride.picked_up_customer && <span className="text-emerald-600">✓ Passenger picked</span>}
          </div>
          {ride.customer_phone && (
            <a 
              href={`tel:${ride.customer_phone}`}
              className="text-blue-600 hover:text-blue-800 flex items-center text-sm font-medium"
              onClick={(e) => e.stopPropagation()}
            >
              <Phone className="h-4 w-4 mr-1" />
              Call
            </a>
          )}
        </div>
      )}

      {/* Vouchers Section */}
      {ride.vouchers && ride.vouchers.length > 0 && (
        <div className="border-t border-slate-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleVouchers(ride.id);
            }}
            className="w-full px-6 py-3 flex items-center justify-between text-sm font-medium text-purple-700 bg-purple-50 hover:bg-purple-100 transition-colors"
            data-testid={`vouchers-toggle-${ride.id}`}
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Vouchers ({ride.vouchers.length})
            </div>
            {expandedRideVouchers[ride.id] ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          
          {expandedRideVouchers[ride.id] && (
            <div className="px-6 py-3 bg-white space-y-2">
              {ride.vouchers.map((voucher) => (
                <button
                  key={voucher.id}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadVoucherWithAuth(ride.id, voucher.id, voucher.filename, token)
                      .then(() => toast.success('Voucher downloaded successfully'))
                      .catch(() => toast.error('Failed to download voucher'));
                  }}
                  className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer"
                  data-testid={`voucher-download-${voucher.id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-purple-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                        {voucher.filename}
                      </p>
                      <p className="text-xs text-slate-400">
                        {new Date(voucher.uploaded_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Download className="h-4 w-4 text-slate-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Port Files Section */}
      {ride.port_files && ride.port_files.length > 0 && (
        <div className="border-t border-slate-200">
          <button
            onClick={(e) => {
              e.stopPropagation();
              togglePortFiles(ride.id);
            }}
            className="w-full px-6 py-3 flex items-center justify-between text-sm font-medium text-cyan-800 bg-cyan-50 hover:bg-cyan-100 transition-colors"
            data-testid={`port-files-toggle-${ride.id}`}
          >
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4" />
              Port ({ride.port_files.length})
            </div>
            {expandedRidePortFiles[ride.id] ? (
              <ChevronUp className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </button>
          
          {expandedRidePortFiles[ride.id] && (
            <div className="px-6 py-3 bg-white space-y-2">
              {ride.port_files.map((portFile) => (
                <button
                  key={portFile.id}
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    downloadPortFileWithAuth(ride.id, portFile.id, portFile.filename, token)
                      .then(() => toast.success('Port file downloaded successfully'))
                      .catch(() => toast.error('Failed to download port file'));
                  }}
                  className="w-full flex items-center justify-between p-3 bg-slate-50 rounded-sm border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer"
                  data-testid={`port-file-download-${portFile.id}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-cyan-600" />
                    <div className="text-left">
                      <p className="text-sm font-medium text-slate-700 truncate max-w-[200px]">
                        {portFile.filename}
                      </p>
                      <p className="text-xs text-slate-400">
                        {portFile.uploaded_at ? new Date(portFile.uploaded_at).toLocaleDateString() : ''}
                      </p>
                    </div>
                  </div>
                  <Download className="h-4 w-4 text-slate-500" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-sky-100 text-sky-800',
      in_progress: 'bg-blue-100 text-blue-800',
      completed: 'bg-emerald-100 text-emerald-800',
      no_show: 'bg-amber-100 text-amber-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-slate-100 text-slate-800';
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-blue-50">
      <ActionStatusOverlay action={successOverlay} onDone={() => setSuccessOverlay(null)} />
      {/* GPS Permission Request Dialog - Only show for location-restricted users */}
      {showGPSPermission && isLocationRestricted && (
        <GPSPermissionRequest
          onPermissionGranted={handleGPSPermissionGranted}
          onPermissionDenied={handleGPSPermissionDenied}
        />
      )}
      
      <header className="bg-white border-b border-blue-200 sticky top-0 z-10 shadow-sm">
        <div className="px-4 sm:px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img 
              src="/logo.jpg" 
              alt="Outsiders Travel" 
              className="h-10 sm:h-12 w-auto object-contain"
            />
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-black" data-testid="driver-dashboard-title">
                My Rides
              </h1>
              <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                <p className="text-xs sm:text-sm text-gray-600 hidden sm:block">Welcome, {user.name}</p>
                {isAdminDriver && (
                  <span className="px-2 py-0.5 bg-purple-600 text-white text-xs rounded-full">
                    Admin-Driver
                  </span>
                )}
              </div>
            </div>
          </div>
          
          {/* Mobile: Show key actions + menu button */}
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Switch to Admin - Always visible for admin-driver on mobile */}
            {isAdminDriver && (
              <Button
                onClick={() => navigate('/admin')}
                className="bg-purple-600 hover:bg-purple-700 text-white text-xs sm:text-sm px-2 sm:px-4"
                data-testid="switch-to-admin-btn"
              >
                <ArrowLeftRight className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Admin View</span>
              </Button>
            )}
            
            {/* Desktop: Show all buttons */}
            <div className="hidden md:flex items-center gap-2">
              <NotificationBell />
              {!hiddenMenu.includes('expenses') && (
              <Button
                onClick={() => navigate('/driver/expenses')}
                variant="ghost"
                className="text-gray-700 hover:text-black hover:bg-blue-50"
              >
                <Euro className="mr-2 h-5 w-5" />
                Expenses
              </Button>
              )}
              {!hiddenMenu.includes('petrol') && (
              <Button
                onClick={() => navigate('/driver/petrol')}
                variant="ghost"
                className="text-gray-700 hover:text-black hover:bg-amber-50"
                data-testid="petrol-button"
              >
                <Fuel className="mr-2 h-5 w-5" />
                Petrol
              </Button>
              )}
              {!hiddenMenu.includes('car_wash') && (
              <Button
                onClick={() => navigate('/driver/car-wash')}
                variant="ghost"
                className="text-gray-700 hover:text-black hover:bg-cyan-50"
                data-testid="car-wash-button"
              >
                <Droplets className="mr-2 h-5 w-5" />
                Car Wash
              </Button>
              )}
              {!hiddenMenu.includes('attendance') && (
              <Button
                onClick={() => navigate('/driver/attendance')}
                variant="ghost"
                className="text-gray-700 hover:text-black hover:bg-emerald-50"
                data-testid="attendance-button"
              >
                <CalendarDays className="mr-2 h-5 w-5" />
                Attendance
              </Button>
              )}
              <Button
                onClick={() => navigate('/driver/settings')}
                variant="ghost"
                className="text-gray-700 hover:text-black hover:bg-blue-50"
                data-testid="settings-button"
              >
                <Settings className="mr-2 h-5 w-5" />
                Settings
              </Button>
              <Button
                onClick={logout}
                variant="ghost"
                className="text-gray-700 hover:text-black hover:bg-blue-50"
                data-testid="logout-button"
              >
                <LogOut className="mr-2 h-5 w-5" />
                Logout
              </Button>
            </div>
            
            {/* Mobile: Menu button */}
            <div className="md:hidden flex items-center gap-1">
              <NotificationBell />
              <Button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                variant="ghost"
                size="icon"
                className="text-gray-700"
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </Button>
            </div>
          </div>
        </div>
        
        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-blue-100 bg-white px-4 py-2 space-y-1">
            {!hiddenMenu.includes('expenses') && (
            <Button
              onClick={() => { navigate('/driver/expenses'); setMobileMenuOpen(false); }}
              variant="ghost"
              className="w-full justify-start text-gray-700 hover:text-black hover:bg-blue-50"
            >
              <Euro className="mr-3 h-5 w-5" />
              Expenses
            </Button>
            )}
            {!hiddenMenu.includes('petrol') && (
            <Button
              onClick={() => { navigate('/driver/petrol'); setMobileMenuOpen(false); }}
              variant="ghost"
              className="w-full justify-start text-gray-700 hover:text-black hover:bg-amber-50"
            >
              <Fuel className="mr-3 h-5 w-5" />
              Petrol
            </Button>
            )}
            {!hiddenMenu.includes('car_wash') && (
            <Button
              onClick={() => { navigate('/driver/car-wash'); setMobileMenuOpen(false); }}
              variant="ghost"
              className="w-full justify-start text-gray-700 hover:text-black hover:bg-cyan-50"
              data-testid="mobile-car-wash-button"
            >
              <Droplets className="mr-3 h-5 w-5" />
              Car Wash
            </Button>
            )}
            {!hiddenMenu.includes('attendance') && (
            <Button
              onClick={() => { navigate('/driver/attendance'); setMobileMenuOpen(false); }}
              variant="ghost"
              className="w-full justify-start text-gray-700 hover:text-black hover:bg-emerald-50"
              data-testid="mobile-attendance-button"
            >
              <CalendarDays className="mr-3 h-5 w-5" />
              My Attendance
            </Button>
            )}
            <Button
              onClick={() => { navigate('/driver/settings'); setMobileMenuOpen(false); }}
              variant="ghost"
              className="w-full justify-start text-gray-700 hover:text-black hover:bg-blue-50"
            >
              <Settings className="mr-3 h-5 w-5" />
              Settings
            </Button>
            <Button
              onClick={logout}
              variant="ghost"
              className="w-full justify-start text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Logout
            </Button>
          </div>
        )}
      </header>

      <DriverWhatsNewModal />

      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="driver-stats">
            <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">In Progress</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.in_progress_rides}</p>
                </div>
                <div className="h-12 w-12 bg-blue-100 rounded-sm flex items-center justify-center">
                  <Play className="h-6 w-6 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Completed</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.completed_rides}</p>
                </div>
                <div className="h-12 w-12 bg-emerald-100 rounded-sm flex items-center justify-center">
                  <CheckCircle className="h-6 w-6 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">No Show</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.no_show_rides || 0}</p>
                </div>
                <div className="h-12 w-12 bg-amber-100 rounded-sm flex items-center justify-center">
                  <XCircle className="h-6 w-6 text-amber-600" />
                </div>
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-sm p-6 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Rides</p>
                  <p className="text-3xl font-bold text-slate-900">{stats.total_rides}</p>
                </div>
                <div className="h-12 w-12 bg-slate-100 rounded-sm flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-slate-600" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Driver Performance Rating Card */}
        <div className="mb-8" data-testid="driver-rating-section">
          <DriverRatingCard />
        </div>

        {/* Daily Vouchers Section */}
        {dailyVouchers.length > 0 && (
          <div className="mb-6 bg-white rounded-lg border border-slate-200 p-4" data-testid="daily-vouchers-section">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="h-5 w-5 text-slate-700" />
              <h3 className="font-semibold text-slate-800">Today's Vouchers</h3>
              <span className="ml-auto text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{dailyVouchers.length}</span>
            </div>
            <div className="space-y-2">
              {dailyVouchers.map(v => (
                <button
                  key={v.id}
                  onClick={async () => {
                    try {
                      const response = await fetch(`${API}/daily-vouchers/${v.id}/download`, {
                        headers: { Authorization: `Bearer ${token}` }
                      });
                      const blob = await response.blob();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = v.filename || 'voucher';
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      window.URL.revokeObjectURL(url);
                    } catch {
                      toast.error('Download failed');
                    }
                  }}
                  className="w-full flex items-center gap-3 bg-slate-50 hover:bg-slate-100 rounded-md px-3 py-2.5 transition-colors text-left"
                  data-testid={`daily-voucher-${v.id}`}
                >
                  <Download className="h-4 w-4 text-blue-500 flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-700 truncate">{v.filename}</p>
                    {v.notes && <p className="text-xs text-slate-400 truncate">{v.notes}</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="mb-6">
          <div className="flex gap-2 flex-wrap" data-testid="status-filter">
            {/* Show "My Rides" filter only for admin-drivers */}
            {isAdminDriver && (
              <Button
                onClick={() => setFilter('my_rides')}
                variant={filter === 'my_rides' ? 'default' : 'outline'}
                className={`rounded-sm ${
                  filter === 'my_rides'
                    ? 'bg-purple-600 text-white hover:bg-purple-700'
                    : 'bg-white text-purple-700 border-purple-300 hover:bg-purple-50'
                }`}
                data-testid="filter-my_rides"
              >
                My Rides
              </Button>
            )}
            {['active', 'in_progress', 'completed', 'no_show', 'cancelled', 'all'].map((status) => (
              <Button
                key={status}
                onClick={() => setFilter(status)}
                variant={filter === status ? 'default' : 'outline'}
                className={`capitalize rounded-sm ${
                  filter === status
                    ? 'bg-slate-900 text-white hover:bg-slate-800'
                    : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
                }`}
                data-testid={`filter-${status}`}
              >
                {status === 'in_progress' ? 'In Progress' : status === 'no_show' ? 'No Show' : status}
              </Button>
            ))}
          </div>
        </div>

        <div className="space-y-6" data-testid="rides-list">
          {filteredRides.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-sm p-12 text-center">
              <MapPin className="h-12 w-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-600">No rides found</p>
            </div>
          ) : (
            <>
              {/* TODAY'S RIDES SECTION */}
              {todayRides.length > 0 && (
                <div>
                  <div className="bg-emerald-50 border-l-4 border-emerald-500 rounded-sm p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-emerald-600" />
                      <h2 className="text-lg font-bold text-emerald-900">
                        TODAY&apos;S RIDES ({todayRides.length})
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {todayRides.map(renderRideCard)}
                  </div>
                </div>
              )}

              {/* TOMORROW'S RIDES SECTION */}
              {tomorrowRides.length > 0 && (
                <div>
                  <div className="bg-purple-50 border-l-4 border-purple-500 rounded-sm p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-purple-600" />
                      <h2 className="text-lg font-bold text-purple-900">
                        TOMORROW&apos;S RIDES ({tomorrowRides.length})
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {tomorrowRides.map(renderRideCard)}
                  </div>
                </div>
              )}

              {/* FUTURE RIDES SECTION */}
              {futureRides.length > 0 && (
                <div>
                  <div className="bg-blue-50 border-l-4 border-blue-500 rounded-sm p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-blue-600" />
                      <h2 className="text-lg font-bold text-blue-900">
                        FUTURE RIDES ({futureRides.length})
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {futureRides.map(renderRideCard)}
                  </div>
                </div>
              )}

              {/* PAST RIDES SECTION */}
              {pastRides.length > 0 && (
                <div>
                  <div className="bg-slate-50 border-l-4 border-slate-400 rounded-sm p-4 mb-4">
                    <div className="flex items-center gap-2">
                      <Clock className="h-5 w-5 text-slate-600" />
                      <h2 className="text-lg font-bold text-slate-900">
                        PAST RIDES ({pastRides.length})
                      </h2>
                    </div>
                  </div>
                  <div className="space-y-4">
                    {pastRides.map(renderRideCard)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}